import React from "react";

/**
 * A minimal resizable split container.
 *
 * Why not `react-split`/`split.js`: split.js captures `window` at module load (the *parent* window) and
 * binds its drag `mousemove`/`mouseup` there, so dragging never works inside the Canvas Debugger pop-out
 * (a separate window that shares the parent's JS context). This implementation uses Pointer Events with
 * `setPointerCapture`, which routes all move/up events to the gutter element within its own document —
 * correct in any window. All layout styles are inline, so it also survives the pop-out's one-time
 * stylesheet snapshot.
 *
 * Sizes are percentages that sum to 100; children are laid out with `flex-grow` (basis 0), so a gutter
 * drag only re-distributes the two adjacent panes (their combined percentage stays fixed).
 */
type Props = {
    direction: "horizontal" | "vertical";
    initialSizes: number[];
    minSizes?: number[];
    gutterSize?: number;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
};

type DragState = { index: number; startPos: number; startA: number; startB: number; pairPct: number; totalPx: number };

function Gutter({ horizontal, size, active, ...handlers }: {
    horizontal: boolean;
    size: number;
    active: boolean;
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
}) {
    const [hovered, setHovered] = React.useState(false);
    return (
        <div
            className={`resize-gutter ${horizontal ? "gutter-horizontal" : "gutter-vertical"}`}
            onPointerDown={handlers.onPointerDown}
            onPointerMove={handlers.onPointerMove}
            onPointerUp={handlers.onPointerUp}
            onPointerCancel={handlers.onPointerUp}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            style={{
                flex: `0 0 ${size}px`,
                cursor: horizontal ? "col-resize" : "row-resize",
                touchAction: "none",
                background: hovered || active ? "var(--primary-color)" : "var(--border-color)",
            }}
        />
    );
}

export function ResizeSplit({ direction, initialSizes, minSizes = [], gutterSize = 6, className, style, children }: Props) {
    const items = React.Children.toArray(children);
    const n = items.length;
    const [sizes, setSizes] = React.useState<number[]>(initialSizes);
    const drag = React.useRef<DragState | null>(null);
    const [activeGutter, setActiveGutter] = React.useState<number | null>(null);
    const horizontal = direction === "horizontal";

    const currentSizes = sizes.length === n ? sizes : initialSizes;

    const onPointerDown = (index: number) => (e: React.PointerEvent<HTMLDivElement>) => {
        const gutter = e.currentTarget;
        const a = gutter.previousElementSibling as HTMLElement | null;
        const b = gutter.nextElementSibling as HTMLElement | null;
        if (!a || !b) return;
        e.preventDefault();
        gutter.setPointerCapture(e.pointerId);
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        const startA = horizontal ? ra.width : ra.height;
        const startB = horizontal ? rb.width : rb.height;
        drag.current = {
            index,
            startPos: horizontal ? e.clientX : e.clientY,
            startA,
            startB,
            pairPct: currentSizes[index] + currentSizes[index + 1],
            totalPx: startA + startB,
        };
        setActiveGutter(index);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const d = drag.current;
        if (!d) return;
        const pos = horizontal ? e.clientX : e.clientY;
        const delta = pos - d.startPos;
        const minA = minSizes[d.index] ?? 40;
        const minB = minSizes[d.index + 1] ?? 40;
        let newA = d.startA + delta;
        let newB = d.startB - delta;
        if (newA < minA) { newA = minA; newB = d.totalPx - newA; }
        if (newB < minB) { newB = minB; newA = d.totalPx - newB; }
        const pctA = d.pairPct * (newA / d.totalPx);
        const pctB = d.pairPct - pctA;
        setSizes(prev => {
            const base = prev.length === n ? [...prev] : [...initialSizes];
            base[d.index] = pctA;
            base[d.index + 1] = pctB;
            return base;
        });
    };

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (drag.current == null) return;
        drag.current = null;
        setActiveGutter(null);
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* pointer already released */ }
    };

    return (
        <div
            className={`resize-split ${className ?? ""}`}
            style={{
                display: "flex",
                flexDirection: horizontal ? "row" : "column",
                minWidth: 0,
                minHeight: 0,
                userSelect: activeGutter != null ? "none" : undefined,
                ...style,
            }}
        >
            {items.map((child, i) => {
                // Inject layout onto the child directly (no wrapper) so gutter siblings are the panes,
                // and so the styles are inline (robust in the pop-out).
                // A nested split manages its own overflow — only leaf panes scroll (avoids stray
                // scrollbars on split containers from sub-pixel rounding).
                const isNestedSplit = React.isValidElement(child) && child.type === ResizeSplit;
                const paneStyle: React.CSSProperties = {
                    flex: `${currentSizes[i]} 1 0`,
                    minWidth: horizontal ? (minSizes[i] ?? 0) : 0,
                    minHeight: horizontal ? 0 : (minSizes[i] ?? 0),
                    overflow: isNestedSplit ? "hidden" : "auto",
                    ...(React.isValidElement(child) ? (child.props as any).style : undefined),
                };
                return (
                    <React.Fragment key={i}>
                        {React.isValidElement(child)
                            ? React.cloneElement(child as React.ReactElement<any>, { style: paneStyle })
                            : child}
                        {i < n - 1 && (
                            <Gutter
                                horizontal={horizontal}
                                size={gutterSize}
                                active={activeGutter === i}
                                onPointerDown={onPointerDown(i)}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default ResizeSplit;
